<template>
    <span :class="['label-badge', classes]" :role="clickable ? 'button' : 'status'"
        :aria-pressed="isBoolean ? (value ? 'true' : 'false') : undefined" :aria-label="ariaLabelComputed"
        :tabindex="clickable ? 0 : -1" :title="tooltip" @click="$emit('click', value)"
        @keydown.enter="$emit('click', value)">

        <span v-if="$slots.icon" class="label-badge-icon">
            <slot name="icon" />
        </span>
        <i v-else-if="icon" :class="icon" aria-hidden="true" class="label-badge-icon"></i>

        <slot>
            {{ displayText }}
        </slot>
    </span>
</template>

<script setup>
import { computed, toRefs } from 'vue'

// Props
const props = defineProps({

    value: { type: [Boolean, String, Number], default: false },


    booleanLabels: {
        type: Object,
        default: () => ({ true: 'Sim', false: 'Não' })
    },


    variant: { type: String, default: null },

    size: { type: String, default: 'md' },

    pill: { type: Boolean, default: true },

    clickable: { type: Boolean, default: false },

    outline: { type: Boolean, default: false },

    icon: { type: String, default: '' },

    tooltip: { type: String, default: '' },

    ariaLabel: { type: String, default: '' },

    framework: { type: String, default: 'bootstrap' }
})

const { value, booleanLabels, variant, size, pill, clickable, outline, icon, tooltip, ariaLabel, framework } = toRefs(props)

const isBoolean = computed(() => typeof value.value === 'boolean')

const computedVariant = computed(() => {
    if (variant.value) return variant.value
    if (isBoolean.value) return value.value ? 'success' : 'danger'
    return 'secondary'
})

const displayText = computed(() => {
    if (isBoolean.value) return value.value ? booleanLabels.value.true : booleanLabels.value.false
    return value.value === null || value.value === undefined ? '' : String(value.value)
})

const ariaLabelComputed = computed(() => {
    if (ariaLabel.value) return ariaLabel.value
    return isBoolean.value ? `Indicador: ${displayText.value}` : `Etiqueta: ${displayText.value}`
})

const classes = computed(() => {
    const base = []

    base.push('px-2', 'py-1', 'fw-bold')

    if (size.value === 'sm') base.push('label-sm')
    if (size.value === 'md') base.push('label-md')
    if (size.value === 'lg') base.push('label-lg')

    if (pill.value) base.push('rounded-pill')
    else base.push('rounded')

    if (clickable.value) base.push('label-clickable')

    const v = computedVariant.value
    if (framework.value === 'tailwind') {

        const map = {
            success: ['bg-green-600', 'text-white'],
            danger: ['bg-red-600', 'text-white'],
            warning: ['bg-yellow-500', 'text-black'],
            info: ['bg-sky-600', 'text-white'],
            primary: ['bg-blue-600', 'text-white'],
            secondary: ['bg-gray-600', 'text-white']
        }
        base.push(...(map[v] || map.secondary))
    } else {

        const map = {
            success: ['bg-success', 'text-white'],
            danger: ['bg-danger', 'text-white'],
            warning: ['bg-warning', 'text-dark'],
            info: ['bg-info', 'text-dark'],
            primary: ['bg-primary', 'text-white'],
            secondary: ['bg-secondary', 'text-white']
        }
        base.push(...(map[v] || map.secondary))

        if (outline.value) {

            const idx = base.findIndex(c => c.startsWith('bg-'))
            if (idx !== -1) {
                const bg = base[idx]
                const color = bg.replace('bg-', '')
                base.splice(idx, 1, `border`, `border-${color}`, `text-${color}`)

                const twIdx = base.indexOf('text-white')
                if (twIdx !== -1) base.splice(twIdx, 1)
            }
        }
    }

    return base.join(' ')
})
</script>

<style scoped>
.label-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    vertical-align: middle;
    user-select: none;
}

.label-badge .label-badge-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}

.label-sm {
    font-size: 0.75rem;
    padding: 0.125rem 0.35rem;
}

.label-md {
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
}

.label-lg {
    font-size: 1rem;
    padding: 0.35rem 0.75rem;
}

.label-clickable {
    cursor: pointer;
}

.label-clickable:focus {
    outline: 2px solid rgba(0, 0, 0, 0.12);
    outline-offset: 2px;
}

.label-badge {
    border-radius: 5px !important;
}
</style>
